import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Input } from '@/shared/ui';
import { track, type AuthOrigem } from '@/lib/analytics';
import type { AuthUser, SignInInput } from '../../domain/entities/auth.entity';
import { useAuth } from '../hooks/use-auth';

interface SignInFormProps {
  /** De onde a pessoa chegou: vai no evento de login. */
  origem: AuthOrigem;
  initialEmail?: string;
  /** Recado de quem chegou desviado do cadastro ("este e-mail já tem conta"). */
  notice?: string;
  /** Recebe a sessão: quem hospeda decide se ainda falta o cadastro daqui. */
  onSuccess: (user: AuthUser) => void;
  onForgotPassword: (email: string) => void;
}

/**
 * O login, como o `SignInForm` do `enamed-feedback-lp`: e-mail, senha e o
 * "Esqueci minha senha" levando o e-mail digitado junto, para a pessoa não
 * reescrever a única coisa que lembra.
 */
export function SignInForm({
  origem,
  initialEmail,
  notice,
  onSuccess,
  onForgotPassword,
}: SignInFormProps) {
  const auth = useAuth();
  const signIn = useMutation({
    mutationFn: (input: SignInInput) => auth.signIn(input),
    onSuccess: (user) => {
      track({ event: 'login', method: 'email', origem });
      onSuccess(user);
    },
    onError: () => track({ event: 'login_erro', origem }),
  });
  const [email, setEmail] = useState(() => initialEmail ?? '');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    signIn.mutate({ email, password });
  };

  return (
    <form onSubmit={handleSubmit} className="form-stack">
      {notice && <div className="notice">{notice}</div>}

      <Input
        id="signInEmail"
        label="E-mail"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="seu@email.com"
      />

      <div>
        <Input
          id="signInPassword"
          label="Senha"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
        />
        <button
          type="button"
          className="link-button field-link"
          onClick={() => onForgotPassword(email)}
        >
          Esqueci minha senha
        </button>
      </div>

      {signIn.isError && (
        <div className="alert" role="alert">
          {signIn.error.message}
        </div>
      )}

      <Button type="submit" disabled={signIn.isPending}>
        {signIn.isPending ? 'Entrando…' : 'Entrar'}
      </Button>
    </form>
  );
}
